import React, { useCallback } from 'react';

import DialogContent from 'components/DialogContent';
import { H2 } from 'components/Heading';
import { ModalFooterButton } from 'components/Modal';
import { FacebookSocialCircle } from 'components/SocialCircle';
import { Step } from 'components/SteppedModal';
import useFacebookAuthenticator, {
  UseFacebookAuthenticatorConfig,
} from 'hooks/useFacebookAuthenticator';
import { block } from '../utils';
import { FacebookAutomationModalStep } from './types';

interface UseAuthorizationStepConfig
  extends Pick<UseFacebookAuthenticatorConfig, 'onAuthSuccess' | 'onError'> {
  onAuth: () => void;
}

export default function useAuthorizationStep({
  onAuth,
  onAuthSuccess,
  onError,
}: UseAuthorizationStepConfig): Step<FacebookAutomationModalStep> {
  const { authenticate, authenticating } = useFacebookAuthenticator({
    force: true,
    onAuthSuccess,
    onError,
  });

  const handleAuthenticate = useCallback((): void => {
    onAuth();
    authenticate();
  }, [authenticate, onAuth]);

  return {
    id: 'authorization',
    fluidFooterButtons: true,
    component: (
      <DialogContent
        title="Let's add your account"
        details={
          <H2>
            We'll quickly send you to Facebook so you can give us permission to
            add videos to your account.
          </H2>
        }
        icon={<FacebookSocialCircle className={block('icon')} />}
        detailsClassName={block('details')}
      />
    ),
    renderFooterButtons: ({ submit }) => [
      <ModalFooterButton
        {...submit}
        theme="submit"
        disabled={authenticating}
        onClick={handleAuthenticate}
      >
        <span>Sign in with Facebook</span>
      </ModalFooterButton>,
    ],
  };
}
